import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { InputField } from "../../Shared/InputField"
import classnames from 'classnames'
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { FaSms } from "react-icons/fa"

const MessageDeliverySummary = () => {
    const [errors, setErrors] = useState({})
    const [data, setData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const gridRef = useRef();
    const navigate = useNavigate()
    const location = useLocation()

    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
    })
    const {fdate, tdate} = formData

    const fetchData = async (fdate, tdate) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/messages/delivery-summary/?fdate=${fdate}&tdate=${tdate}`)
            setData(response?.data)
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load delivery summary')
            }
        }
    }

    const resendPending = async (payload) => {
        setIsLoading(true);
        try {
            const response = await Server.post('/messages/resend-pending-messages', payload)
            toast.success(response.data.detail);
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false);
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to resend pending messages')
            }
        }
    }

    useEffect(() => {
        if (location.state?.fdate && location.state?.tdate) {
            setFormData(prev => ({
                ...prev,
                fdate: location.state.fdate,
                tdate: location.state.tdate,
            }))
            fetchData(location.state.fdate, location.state.tdate)
        } else {
            fetchData(fdate, tdate)
        }
    }, [])

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onSubmit = (e) => {
        e.preventDefault()

        fetchData(fdate, tdate)

    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Category",
            field: "category",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Sent",
            field: "sent",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Delivered",
            cellRenderer: (e) => {
                if (!e.data) return null;
                return <Link title="View delivered messages" to="/message-delivery-detail" state={{ payload: {
                    status: 'Delivered',
                    fdate: fdate,
                    tdate: tdate,
                    category_id: e.data.id,
                    name: e.data.category
                }}}>{e.data.delivered}</Link>
            },
            sortable: false,
            filter: false,
          },
        {
            headerName: "Pending",
            cellRenderer: (e) => {
                if (!e.data) return null;
                return <Link title="View pending messages" to="/message-delivery-detail" state={{ payload: {
                    status: 'Pending',
                    fdate: fdate,
                    tdate: tdate,
                    category_id: e.data.id,
                    name: e.data.category
                }}}>{e.data.pending}</Link>
            },
            sortable: false,
            filter: false,
        },
        {
            headerName: "Resend",
            cellRenderer: (e) => {
                if (!e.data) return null;
                return (
                    <button
                        className={classnames("btn", "btn-outline-info", { "disabled": e.data.pending < 1 })}
                        onClick={() => { if (e.data.pending < 1) return; resendPending({
                            category_id: e.data.id,
                            fdate: fdate,
                            tdate: tdate
                        }); }}
                        disabled={e.data.pending < 1}
                        title="Resend pending messages"
                    >
                        Resend
                    </button>
                );
            },
            sortable: false,
            filter: false,
        }
    ], [fdate, tdate]);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                    />
                </div>
                <div className="col-md-8">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">Message Delivery Report Summary {fdate} To {tdate}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}

                            rowData={data}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}

                            ref={gridRef}
                            groupIncludeTotalFooter={false}
                            groupIncludeFooter={false}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default MessageDeliverySummary